<script setup>
import { computed, ref } from "vue";
import { router } from "@inertiajs/vue3";
import { useAuthUser } from "@/composables/useAuthUser.js";
import BrandButton from "@/Components/Brand/BrandButton.vue";
import { useInertiaAction } from "@/composables/useInertiaAction.js";

const props = defineProps({
    owner: {
        type: Object,
        required: true,
    },
    // Bare "Follow"/"Unfollow" with no glyph, for tight slots such as the
    // About page team cards (GH-1145).
    compact: {
        type: Boolean,
        default: false,
    },
});

const authUser = useAuthUser();

const { busy: followBusy, run: runFollow } = useInertiaAction();

const label = computed(() => {
    if (props.compact) {
        if (followBusy.value) {
            return props.owner.following ? "Unfollowing" : "Following";
        }

        return props.owner.following ? "Unfollow" : "Follow";
    }

    const shortName = props.owner.is_organization
        ? props.owner.presentation_name
        : props.owner.first_name || props.owner.presentation_name;

    // Progressive verb while the request runs (GH-1001); owner.following
    // still holds the pre-toggle state until the reload lands.
    if (followBusy.value) {
        return props.owner.following ? "Unfollowing" : `Following ${shortName}`;
    }

    if (props.owner.following) {
        return "Unfollow";
    }

    return `Follow ${shortName}`;
});

function handleClick() {
    if (!authUser.value) {
        router.get(route("register"));

        return;
    }

    toggleFollow();
}

// The endpoints return a bare boolean (not an Inertia response), so this
// goes through axios and reloads props afterwards.
async function toggleFollow() {
    if (followBusy.value) {
        return;
    }

    // Busy spans the request and the reload (GH-1159) so the button lands
    // directly in its new state.
    await runFollow(() =>
        props.owner.following
            ? axios.delete(route("user.unfollow", { user: props.owner.uuid }))
            : axios.post(route("user.follow", { user: props.owner.uuid })),
    );
}
</script>

<template>
    <BrandButton
        :variant="owner.following ? 'whiteOnRed' : 'navy'"
        icon-rest-class="text-brand-red"
        class="h-[52px] px-6 text-[15px]"
        :class="owner.following && 'ring-1 ring-brand-tan'"
        :loading="followBusy"
        @click="handleClick"
    >
        <template v-if="!compact" #icon>
            <!-- Even height keeps the centering offset integral inside the
                 52px button; odd sizes rasterize a half-pixel apart between
                 the resting layer and the hover-lift composite, making the
                 glyph jiggle on hover. -->
            <svg
                viewBox="0 0 21 15"
                class="h-4 w-[22px] shrink-0"
                fill="currentColor"
                aria-hidden="true"
            >
                <path
                    d="M3.81818 10.3125L3.24545 9.84375C1.33636 8.0625 0 6.9375 0 5.53125C0 4.40625 0.954545 3.46875 2.1 3.46875C2.76818 3.46875 3.43636 3.75 3.81818 4.21875C4.2 3.75 4.86818 3.46875 5.53636 3.46875C6.68182 3.46875 7.63636 4.3125 7.63636 5.53125C7.63636 6.9375 6.3 8.0625 4.39091 9.84375L3.81818 10.3125ZM13.3636 0C12.351 0 11.3798 0.395088 10.6638 1.09835C9.94773 1.80161 9.54545 2.75544 9.54545 3.75C9.54545 4.74456 9.94773 5.69839 10.6638 6.40165C11.3798 7.10491 12.351 7.5 13.3636 7.5C14.3763 7.5 15.3474 7.10491 16.0635 6.40165C16.7795 5.69839 17.1818 4.74456 17.1818 3.75C17.1818 2.75544 16.7795 1.80161 16.0635 1.09835C15.3474 0.395088 14.3763 0 13.3636 0ZM13.3636 5.71875C12.832 5.71875 12.3221 5.51133 11.9462 5.14212C11.5703 4.7729 11.3591 4.27214 11.3591 3.75C11.3591 3.22785 11.5703 2.7271 11.9462 2.35788C12.3221 1.98867 12.832 1.78125 13.3636 1.78125C13.6269 1.78125 13.8875 1.83217 14.1307 1.93111C14.3739 2.03005 14.5949 2.17507 14.7811 2.35788C14.9672 2.5407 15.1149 2.75773 15.2156 2.99659C15.3163 3.23545 15.3682 3.49146 15.3682 3.75C15.3682 4.00854 15.3163 4.26455 15.2156 4.50341C15.1149 4.74227 14.9672 4.9593 14.7811 5.14212C14.5949 5.32493 14.3739 5.46995 14.1307 5.56889C13.8875 5.66783 13.6269 5.71875 13.3636 5.71875ZM13.3636 8.4375C10.815 8.4375 5.72727 9.68437 5.72727 12.1875V15H21V12.1875C21 9.68437 15.9123 8.4375 13.3636 8.4375ZM19.1864 13.2188H7.54091V12.1875C7.54091 11.5875 10.5 10.2188 13.3636 10.2188C16.1986 10.2188 19.1864 11.5875 19.1864 12.1875V13.2188Z"
                />
            </svg>
        </template>
        <span class="min-w-0 truncate">{{ label }}</span>
    </BrandButton>
</template>
